import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../Components/Button";
import Select from "react-select";
import {
  clearBusinessPartner,
  requestCreateBusinessPartner,
  requestListBusiness,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import InputSkeleton from "../../Components/InputSkeleton";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { classNames, getErrorMessages } from "../../Utils";
import { NumericFormat } from "react-number-format";
import FormPhoneInput from "../../Components/v2/Form/FormPhoneInput";
import FormLabel from "../../Components/FormLabel";

const businessTypes = [
  { label: "Personal", value: "PERSONAL" },
  { label: "Incorporated", value: "BUSINESS" },
];

export const PartnerCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedBusiness, setselectedBusiness] = useState(null);
  const [partnerName, setpartnerName] = useState("");
  const [email, setemail] = useState("");
  const [type, settype] = useState("PERSONAL");
  const [phone, setphone] = useState();
  const [npwp, setnpwp] = useState("");
  const [address, setaddress] = useState("");
  const [fieldError, setfieldError] = useState(false);
  const [showSuccessModal, setshowSuccessModal] = useState(false);
  const [emailError, setemailError] = useState(null);
  const [phoneError, setphoneError] = useState(null);
  const [nameError, setnameError] = useState(null);
  const [npwpError, setnpwpError] = useState(null);
  const [submitted, setsubmitted] = useState(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("id");
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const cleanedPhone = phone?.replace(/\s/g, "");

  const businessTransactionDispatch = useBusinessTransactionDispatch();

  const {
    businesses,
    businessLoading,
    newPartner,
    newPartnerLoading,
    newPartnerError,
  } = useBusinessTransactionState();

  const { user } = useAuthState();

  useEffect(() => {
    if (user.id) {
      requestListBusiness(businessTransactionDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [businessTransactionDispatch, user.id]);

  useEffect(() => {
    if (businesses.length > 0) {
      if (businessIdParam) {
        const business = businesses.find(
          (item) => item.value === businessIdParam
        );
        if (business) {
          setselectedBusiness({
            ...business,
            label: business.name,
            value: business.id,
          });
        }
      } else {
        setselectedBusiness(businesses[0]);
      }
    }
  }, [businessIdParam, businesses]);

  useEffect(() => {
    if (newPartner) {
      setshowSuccessModal(true);
    }
  }, [newPartner]);

  useEffect(() => {
    if (submitted) {
      const isNameValid = partnerName.length >= 2;
      if (isNameValid) {
        setErrorMessage("name", "");
      } else {
        setErrorMessage(
          "name",
          "Partner name must be at least 2 characters long."
        );
      }
    }
  }, [partnerName, submitted]);

  useEffect(() => {
    if (submitted && phone) {
      const isPhoneValid = cleanedPhone?.length >= 10;
      if (isPhoneValid) {
        setErrorMessage("phone", "");
      } else {
        setErrorMessage("phone", "Please enter a valid phone number.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, submitted]);

  useEffect(() => {
    if (submitted && email) {
      const isEmailValid = emailRegex.test(email);
      if (isEmailValid) {
        setErrorMessage("email", "");
      } else {
        setErrorMessage("email", "Please enter a valid email address.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, submitted]);

  useEffect(() => {
    if (submitted && npwp) {
      const isNpwpValid = npwp.length >= 15 && npwp.length <= 16;
      if (isNpwpValid) {
        setErrorMessage("npwp", "");
      } else {
        setErrorMessage("npwp", "Invalid NPWP. Enter 15-16 digits.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npwp, submitted]);

  const validateInput = () => {
    let currentValidName = true;
    let currentValidPhone = true;
    let currentValidEmail = true;
    let currentValidNpwp = true;

    const isEmailValid = emailRegex.test(email);
    const isPhoneValid = cleanedPhone?.length >= 10;
    const isNameValid = partnerName.length >= 2;
    const isNpwpValid = npwp.length >= 15 && npwp.length <= 16;

    currentValidName = isNameValid;

    if (phone) {
      currentValidPhone = isPhoneValid;
    }

    if (email) {
      currentValidEmail = isEmailValid;
    }

    if (npwp) {
      currentValidEmail = isNpwpValid;
    }

    return (
      currentValidName &&
      currentValidPhone &&
      currentValidEmail &&
      currentValidNpwp
    );
  };

  const onSave = () => {
    setsubmitted(true);
    const validForm = validateInput();

    if (businessIdParam && partnerName) {
      setfieldError(false);
    } else {
      setfieldError(true);
      return;
    }

    if (validForm) {
      const input = {
        businessID: businessIdParam,
        name: partnerName,
        email,
        phone: cleanedPhone,
        type,
        taxNumber: npwp,
        address,
      };

      requestCreateBusinessPartner(businessTransactionDispatch, { input });
    }
  };

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "email":
        setemailError(message);
        break;
      case "phone":
        setphoneError(message);
        break;
      case "name":
        setnameError(message);
        break;
      case "npwp":
        setnpwpError(message);
        break;
      default:
        break;
    }
  };

  const navigateToPartner = () => {
    setshowSuccessModal(false);
    if (!newPartnerError) {
      if (newPartner) {
        navigate(`/partner?id=${businessIdParam}`);
        clearBusinessPartner(businessTransactionDispatch);
      }
    }
  };

  return (
    <div className="bg-white border border-gray-200 divide-y divide-gray-200 shadow sm:overflow-hidden rounded-md sm:rounded-lg">
      <form onSubmit={onSave}>
        <div className="space-y-6 px-4 py-6 sm:p-6">
          {newPartner && (
            <ModalConfirm
              open={showSuccessModal}
              setOpen={(open) => {
                setshowSuccessModal(open);
              }}
              title="Success"
              description="Your new partner has been created."
              type="SUCCESS"
              onConfirm={navigateToPartner}
            />
          )}
          {fieldError && (
            <Alert
              title="Incomplete Form"
              type="warning"
              message="Please fill out all fields."
            />
          )}
          {newPartnerError && (
            <Alert
              title="Error"
              type="error"
              message={
                t(getErrorMessages(newPartnerError)) || "Error create Partner."
              }
              errors={newPartnerError?.errors}
            />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {newPartnerLoading ? (
              <InputSkeleton rows={4} />
            ) : (
              <>
                <div>
                  <FormLabel
                    htmlFor="business"
                    label={t("Business Name")}
                    required
                  />
                  <Select
                    name="business"
                    className="mt-1 text-sm"
                    classNamePrefix="select2-selection"
                    options={businesses}
                    isLoading={businessLoading}
                    isDisabled={businessLoading}
                    placeholder={t("Select Business Name")}
                    value={selectedBusiness || ""}
                    onChange={setselectedBusiness}
                  />
                </div>
                <div>
                  <FormLabel htmlFor="type" label={t("Business Type")} />
                  <select
                    id="type"
                    name="type"
                    value={type}
                    onChange={(e) => settype(e.target.value)}
                    className="mt-1 block w-full text-sm rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                  >
                    {businessTypes.map((businessType) => (
                      <option
                        key={businessType.value}
                        value={businessType.value}
                      >
                        {t(businessType.label)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <FormLabel htmlFor="name" label={t("Name")} required />
                  <input
                    type="text"
                    name="name"
                    value={partnerName}
                    placeholder={t("Enter Partner Name")}
                    maxLength={120}
                    onChange={(e) => setpartnerName(e.target.value)}
                    className={classNames(
                      nameError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm"
                    )}
                  />
                  {nameError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(nameError)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel
                    htmlFor="npwp"
                    label={t("Tax Identification Number (NPWP)")}
                  />
                  <NumericFormat
                    type="text"
                    name="npwp"
                    id="npwp"
                    allowLeadingZeros
                    className={classNames(
                      npwpError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm"
                    )}
                    placeholder={t(
                      "Enter the tax identification number (NPWP)"
                    )}
                    displayType="input"
                    value={npwp || ""}
                    onValueChange={(value) => {
                      setnpwp(value.value);
                    }}
                    isAllowed={({ value }) => value.length <= 16}
                  />
                  {npwpError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(npwpError)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel htmlFor="email" label={t("Email")} />
                  <input
                    type="email"
                    name="email"
                    value={email}
                    placeholder={t("Enter email")}
                    onChange={(e) => setemail(e.target.value)}
                    className={classNames(
                      emailError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm"
                    )}
                  />
                  {emailError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(emailError)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel htmlFor="phone" label={t("Phone Number")} />
                  <FormPhoneInput
                    id="phone"
                    name="phone"
                    value={phone}
                    onChange={setphone}
                    placeholder={t("Enter Phone Number")}
                    className={classNames(
                      phoneError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm"
                    )}
                  />
                  {phoneError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(phoneError)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel htmlFor="address" label={t("Address")} />
                  <input
                    id="address"
                    type="text"
                    name="address"
                    value={address}
                    onChange={(e) => setaddress(e.target.value)}
                    placeholder={t("Enter Address")}
                    className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className="bg-gray-50 rounded-b-md sm:rounded-b-lg px-4 py-6 text-right sm:px-6">
          <Button
            type="submit"
            className="w-full sm:w-auto"
            title={t("Create Partner")}
            loading={newPartnerLoading}
            disabled={newPartnerLoading || Boolean(partnerName === "")}
            onClick={(e) => {
              e.preventDefault();
              onSave();
            }}
          />
        </div>
      </form>
    </div>
  );
};
